import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Offcanvas, Form } from "react-bootstrap";
import { MdArrowBack } from "react-icons/md";
import * as apicall from "../../components/Apicall";
import Skeleton from "../../components/Loader/Skeleton";
import { toast } from "react-toastify";
import serviceNames from "../../data/serviceNames.json";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const ViewProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditSidebar, setShowEditSidebar] = useState(false);

  const getRandomColor = () => {
    const colors = [
      "#FFE4E1", // Misty Rose
      "#E6E6FA", // Lavender
      "#F0FFF0", // Honeydew
      "#F0F8FF", // Alice Blue
      "#FFF0F5", // Lavender Blush
      "#F5F5DC", // Beige
      "#E0FFFF", // Light Cyan
      "#FFE4B5", // Moccasin
      "#F0FFFF", // Azure
      "#F8F8FF", // Ghost White
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    let initials = names[0].charAt(0).toUpperCase();
    if (names.length > 1) {
      initials += names[names.length - 1].charAt(0).toUpperCase();
    }
    return initials;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);

        const response = await apicall.get(`ezpMyProfile?user_id=${id}`);
        if (response.status === 200 && response.data?.length > 0) {
          const user = response.data[0];

          const transformedData = {
            personalDetails: {
              fullName: `${user.first_name || ""} ${
                user.last_name || ""
              }`.trim(),
              gender: user.gender === 1 ? "Male" : "Female",
              dateOfBirth: user.dob || "",
              role: user.role || "",
              vendorName: user.vendor_name || "",
            },
            addressDetails: {
              address: user.vendor_address || "",
              city: user.vendor_city || "",
              state: user.vendor_state || "",
              country: user.vendor_country || "",
            },
            contactDetails: {
              phoneNumber: user.phone_number || "",
              email: user.email || "",
            },
            profileImage:
              user.profile_image ||
              "https://via.placeholder.com/300x300?text=No+Image",
          };

          setUserData(transformedData);
          setError(null);
        } else {
          console.error("Invalid response format:", response);
          throw new Error("No user data found");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditProfile = () => {
    setShowEditSidebar(true);
  };

  const getServiceName = (names, serviceId) => {
    const service = serviceNames.service_names.find(
      (s) => s.service_id === Number(serviceId)
    ); // Default to 7 for doctors
    return service ? service[names] : "";
  };

  const handleCreateProfile = async () => {
    try {
      const response = await apicall.get(`get_service_providers?user_id=${id}`);
      if (response.status === 200 && response.data?.length > 0) {
        const doctorData = response.data[0];
        // Calculate years of experience
        const calculateExperience = (practiceStart) => {
          if (!practiceStart) return "0 Years";
          const startDate = new Date(practiceStart);
          const currentDate = new Date();
          const years = currentDate.getFullYear() - startDate.getFullYear();
          return `${years} Years`;
        };
        const Genderform = (genderValue) => {
          if (genderValue === 1) return "Male";
          if (genderValue === 2) return "Female";
          return genderValue === 1 ? "Male" : "Female"; // fallback
        };

        navigate(`/users/profile/${id}`, {
          state: {
            doctorData: {
              id: doctorData.id,
              profile_image: doctorData.profile_image,
              name: `${doctorData.first_name} ${
                doctorData.last_name || ""
              }`.trim(),
              specialty: doctorData.specilisation_name,
              experience: calculateExperience(doctorData.practice_start),
              education: doctorData.education,
              rating: doctorData.average_rating || "0.0",
              serviceName: getServiceName("name", doctorData.service),
              serviceShortName: getServiceName("shortName", doctorData.service),
              reviews: doctorData.total_bookings || 0,
              email: doctorData.email,
              phone_number: doctorData.phone_number,
              address: doctorData.address,
              gender: Genderform(doctorData.gender),
              vendor_city: doctorData.city,
              vendor_state: doctorData.state,
              vendor_country: doctorData.country,
              role: doctorData.role_name,
              vendor_name: doctorData.vendor_name,
              about_user: doctorData.about_user,
              fee: doctorData.fee,
              specilisation: doctorData.specilisation,
              is_active: doctorData.is_active,
              date_of_birth: doctorData?.dob || doctorData?.date_of_birth || "",
              practice_start: doctorData?.practice_start || "",
            },
          },
        });
      } else {
        console.error("No service provider data found");
      }
    } catch (error) {
      console.error("Error fetching service provider data:", error);
    }
  };

  const EditProfileSidebar = () => {
    const [formData, setFormData] = useState({
      fullName: "",
      gender: "",
      dob: "",
      address: "",
      vendor_city: "",
      vendor_state: "",
      vendor_country: "",
      phone_number: "",
      email: "",
    });

    useEffect(() => {
      if (userData) {
        setFormData({
          fullName: userData.personalDetails.fullName,
          gender: userData.personalDetails.gender === "Male" ? "1" : "2",
          dob: userData.personalDetails.dateOfBirth,
          address: userData.addressDetails.address,
          vendor_city: userData.addressDetails.city,
          vendor_state: userData.addressDetails.state,
          vendor_country: userData.addressDetails.country,
          phone_number: userData.contactDetails.phoneNumber,
          email: userData.contactDetails.email,
        });
      }
    }, [userData]);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSubmit = async () => {
      const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      try {
        const [firstName, ...lastNameParts] = formData.fullName
          .trim()
          .split(" ");
        const lastName = lastNameParts.join(" ");

        const payload = {
          first_name: firstName,
          last_name: lastName,
          gender: parseInt(formData.gender),
          dob: formatDate(formData.dob),
          address: formData.address || "",
          phone_number: formData.phone_number || "",
          user_id: id,
          country_id: 0,
          state_id: 0,
          city_id: 0,
          role: formData.role,
        };

        // Only include email in payload if it has changed from original value
        if (formData.email !== userData.contactDetails.email) {
          payload.email = formData.email;
        }

        const response = await apicall.post(payload, "updateUser");

        if (response.status === 200) {
          toast.success("Profile updated successfully", { autoClose: 1500 });
          setShowEditSidebar(false);
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          toast.error(response.message || "Failed to update profile");
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error("Failed to update profile");
      }
    };

    return (
      <Offcanvas
        show={showEditSidebar}
        onHide={() => setShowEditSidebar(false)}
        placement="end"
        style={{ width: "500px" }}
      >
        <Offcanvas.Header closeButton className="custom-border-bottom p-4">
          <Offcanvas.Title className="page-heading">
            Edit Profile
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className="p-4">
            <Form>
              <div className="mb-4">
                <h6 className="profile-section-heading mb-3">
                  Personal Details
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    type="text"
                    name="role"
                    value={userData?.personalDetails?.role || ""}
                    disabled
                    readOnly
                    className="form-input label-bgcolor"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="1">Male</option>
                    <option value="2">Female</option>
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="mb-4">
                <h6 className="profile-section-heading mb-3">
                  Address Details
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label>Address Line</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="form-input label-bgcolor"
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_city"
                    value={formData.vendor_city}
                    onChange={handleInputChange}
                    className="form-input label-bgcolor"
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_state"
                    value={formData.vendor_state}
                    onChange={handleInputChange}
                    className="form-input label-bgcolor"
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_country"
                    value={formData.vendor_country}
                    onChange={handleInputChange}
                    className="form-input label-bgcolor"
                    disabled
                  />
                </Form.Group>
              </div>

              <div className="mb-4">
                <h6 className="profile-section-heading mb-3">
                  Contact Details
                </h6>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <PhoneInput
                    country={"ae"}
                    onlyCountries={["ae", "in"]}
                    value={formData.phone_number}
                    onChange={(value) => {
                      const cleanedValue = value.replace(/\D/g, "");
                      setFormData((prev) => ({
                        ...prev,
                        phone_number: cleanedValue,
                      }));
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      backgroundColor: "#f8f9fa",
                      borderColor: "#E9ECEF",
                      cursor: "not-allowed",
                    }}
                    inputProps={{
                      name: "phone_number",
                      required: true,
                      disabled: true,
                    }}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </Form.Group>
              </div>
            </Form>
          </div>
          <div
            className="custom-border-top bg-white p-4"
            style={{
              position: "sticky",
              bottom: 0,
              display: "flex",
              gap: "12px",
            }}
          >
            <Button
              variant="light"
              onClick={() => setShowEditSidebar(false)}
              className="custom-radius btn-outline-borders"
              style={{ flex: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="btn-custom-danger custom-radius"
              style={{ flex: 1 }}
              onClick={handleSubmit}
            >
              Save Changes
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    );
  };

  const ProfileSkeleton = () => (
    <div className="px-4 py-3">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          className="text-dark text-decoration-none p-0 back-button"
          disabled
        >
          <MdArrowBack size={20} /> BACK
        </Button>
      </div>

      <div className="bg-white rounded-4 border-box p-5">
        <div className="d-flex" style={{ gap: "79px" }}>
          {/* Left Column - Profile Image Skeleton */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "258px",
            }}
          >
            <Skeleton width="258px" height="248px" marginBottom="16px" />
            <Skeleton width="100%" height="40px" marginBottom="0" />
          </div>

          {/* Right Column - User Details Skeleton */}
          <div className="flex-grow-1">
            <div className="row">
              {/* Personal Details Skeleton */}
              <div className="col-md-4">
                <Skeleton width="120px" height="24px" marginBottom="24px" />
                {[...Array(4)].map((_, index) => (
                  <div key={`personal-${index}`} className="mb-3">
                    <Skeleton width="80px" height="16px" marginBottom="8px" />
                    <Skeleton width="100%" height="20px" marginBottom="0" />
                  </div>
                ))}
              </div>

              {/* Address Details Skeleton */}
              <div className="col-md-4">
                <Skeleton width="120px" height="24px" marginBottom="24px" />
                {[...Array(4)].map((_, index) => (
                  <div key={`address-${index}`} className="mb-3">
                    <Skeleton width="80px" height="16px" marginBottom="8px" />
                    <Skeleton width="100%" height="20px" marginBottom="0" />
                  </div>
                ))}
              </div>

              {/* Contact Details Skeleton */}
              <div className="col-md-4">
                <Skeleton width="120px" height="24px" marginBottom="24px" />
                {[...Array(2)].map((_, index) => (
                  <div key={`contact-${index}`} className="mb-3">
                    <Skeleton width="80px" height="16px" marginBottom="8px" />
                    <Skeleton width="100%" height="20px" marginBottom="0" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return <div className="p-4 text-danger">{error}</div>;
  }

  if (!userData) {
    return <div className="p-4">No user data available</div>;
  }

  return (
    <div className="px-4 py-3">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          className="text-dark text-decoration-none p-0 back-button"
          onClick={handleBack}
        >
          <MdArrowBack size={20} /> BACK
        </Button>
      </div>

      <div className="bg-white rounded-4 border-box p-5">
        <div className="d-flex flex-column flex-lg-row" style={{ gap: "30px" }}>
          {/* Left Column - Profile Image */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              maxWidth: "258px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                width: "100%",
                paddingBottom: "96%",
                borderRadius: "20px",
                overflow: "hidden",
                position: "relative",
                backgroundColor:
                  userData.profileImage && userData.profileImage !== "undefined"
                    ? "transparent"
                    : getRandomColor(),
              }}
            >
              {userData.profileImage &&
              userData.profileImage !== "undefined" ? (
                <>
                  {/* Blurred background image */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-5px",
                      left: "-5px",
                      right: "-5px",
                      bottom: "-5px",
                      backgroundImage: `url(${userData.profileImage})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "blur(8px)",
                      opacity: "0.7",
                    }}
                  />
                  {/* Main image */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    <img
                      src={userData.profileImage}
                      alt={userData.personalDetails.fullName}
                      className="profile-image-shadow"
                      onError={(e) => {
                        const parentDiv = e.target.parentElement.parentElement;
                        parentDiv.style.backgroundColor = getRandomColor();
                        parentDiv.innerHTML = `
                          <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                                    display: flex; align-items: center; justify-content: center;
                                    font-size: min(64px, 10vw); color: #666666; font-weight: bold;">
                            ${getInitials(userData.personalDetails.fullName)}
                          </div>`;
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        borderRadius: "20px",
                      }}
                    />
                  </div>
                </>
              ) : (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "min(64px, 10vw)",
                    color: "#666666",
                    fontWeight: "bold",
                  }}
                >
                  {getInitials(userData.personalDetails.fullName)}
                </div>
              )}
            </div>
            <div
              style={{
                width: "100%",
                marginTop: "16px",
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                flexDirection: "row",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="danger"
                className="btn-custom-danger custom-radius"
                style={{
                  flex: "1 1 auto",
                  minWidth: "120px",
                  height: "35px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
                onClick={handleEditProfile}
              >
                Edit Profile
              </Button>
              <Button
                variant="danger"
                className="btn-custom-danger custom-radius"
                style={{
                  flex: "1 1 auto",
                  minWidth: "120px",
                  height: "35px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
                onClick={handleCreateProfile}
              >
                Edit availability
              </Button>
            </div>
          </div>

          {/* Right Column - User Details */}
          <div className="flex-grow-1">
            <div className="row g-4">
              {/* Personal Details */}
              <div className="col-12 col-md-4">
                <h6 className="profile-section-heading mb-4">
                  Personal Details
                </h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="mb-1 label">Full Name</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.personalDetails.fullName}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">Gender</div>
                    <div className="fw-bold content text-black">
                      {userData.personalDetails.gender}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">Role</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.personalDetails.role}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">Vendor</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.personalDetails.vendorName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Details */}
              <div className="col-12 col-md-4">
                <h6 className="profile-section-heading mb-4">
                  Address Details
                </h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="mb-1 label">Address</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.addressDetails.address}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">City</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.addressDetails.city}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">State</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.addressDetails.state}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">Country</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.addressDetails.country}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="col-12 col-md-4">
                <h6 className="profile-section-heading mb-4">
                  Contact Details
                </h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="mb-1 label">Mobile Number</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.contactDetails.phoneNumber}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 label">Email</div>
                    <div className="fw-bold content text-black text-break">
                      {userData.contactDetails.email}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditProfileSidebar />
    </div>
  );
};

export default ViewProfile;
